/**
 * Sefaria API Types
 *
 * Type definitions for the Sefaria API responses and
 * internal reader state management.
 */

// ============================================================
// Core Text Types
// ============================================================

/**
 * A single verse from the Torah text
 */
export interface SefariaVerse {
  /** Reference string, e.g., "Exodus 13:17" */
  ref: string;
  /** Hebrew reference string */
  heRef: string;
  /** Verse number within the chapter */
  verseNumber: number;
  /** Chapter number */
  chapter: number;
  /** Hebrew text with vowels (niqqud) */
  hebrew: string;
  /** English translation */
  english: string;
  /** Whether this verse has linked commentary */
  hasCommentary: boolean;
}

/**
 * Complete parsha text with all verses
 */
export interface SefariaText {
  /** Full reference, e.g., "Exodus 13:17-17:16" */
  ref: string;
  /** Book name, e.g., "Exodus" */
  book: string;
  /** Parsha name, e.g., "Beshalach" */
  parsha: string;
  /** Hebrew parsha name */
  parshaHebrew?: string;
  /** All verses in the parsha */
  verses: SefariaVerse[];
  /** Available translation versions */
  availableVersions: SefariaVersion[];
  /** ISO timestamp when data was fetched */
  fetchedAt: string;
}

/**
 * A translation version available for a text
 */
export interface SefariaVersion {
  /** Full version title */
  versionTitle: string;
  /** Short display title, e.g., "JPS 1985" */
  shortVersionTitle: string;
  /** Language code, e.g., "en", "he" */
  language: string;
  /** Whether this is the primary/default version */
  isPrimary: boolean;
  /** License information */
  license?: string;
}

// ============================================================
// Commentary Types
// ============================================================

/**
 * A linked commentary or reference for a verse
 */
export interface SefariaLink {
  /** Reference to the commentary, e.g., "Rashi on Exodus 13:17:1" */
  ref: string;
  /** Category of the link, e.g., "Commentary", "Midrash" */
  category: string;
  /** Collective title in both languages */
  collectiveTitle: {
    en: string;
    he: string;
  };
  /** Hebrew text of the commentary */
  hebrew: string;
  /** English translation of the commentary */
  english: string;
  /** Source reference that this links from */
  anchorRef?: string;
}

/**
 * Grouped commentaries by category
 */
export interface CommentaryGroup {
  category: string;
  links: SefariaLink[];
}

// ============================================================
// Reader State Types
// ============================================================

/** Layout modes for the reader */
export type ReaderLayout = 'stacked' | 'side-by-side' | 'hebrew-only' | 'english-only';

/** Font size options */
export type ReaderFontSize = 'small' | 'medium' | 'large' | 'xlarge';

/**
 * User preferences for the reader
 */
export interface ReaderSettings {
  /** Text layout mode */
  layout: ReaderLayout;
  /** Font size preference */
  fontSize: ReaderFontSize;
  /** Selected translation version title */
  selectedVersion: string;
}

/**
 * Per-user reader state (persisted)
 */
export interface ReaderUserState {
  /** Array of highlighted verse refs */
  highlightedVerses: string[];
  /** Last reading position (verse ref) */
  lastPosition: string | null;
}

// ============================================================
// Cache Types
// ============================================================

/**
 * Cache entry wrapper with metadata
 */
export interface CacheEntry<T> {
  /** The cached data */
  data: T;
  /** ISO timestamp when data was fetched */
  fetchedAt: string;
  /** ISO timestamp when cache expires */
  expiresAt: string;
  /** Schema version for cache migrations */
  version: number;
}

/**
 * Cache TTL configuration in milliseconds
 */
export const CACHE_TTL = {
  /** Parsha text: 7 days */
  TEXT: 7 * 24 * 60 * 60 * 1000,
  /** Commentary: 30 days */
  COMMENTARY: 30 * 24 * 60 * 60 * 1000,
  /** Version metadata: 30 days */
  VERSIONS: 30 * 24 * 60 * 60 * 1000,
  /** Reading position: 30 days */
  POSITION: 30 * 24 * 60 * 60 * 1000,
} as const;

/** Current cache schema version */
export const CACHE_VERSION = 1;

// ============================================================
// API Response Types (raw from Sefaria)
// ============================================================

/**
 * Raw text response from Sefaria API
 */
export interface SefariaTextApiResponse {
  ref: string;
  heRef: string;
  text: string | string[];
  he: string | string[];
  versions?: SefariaVersionApiResponse[];
  sectionRef?: string;
  indexTitle?: string;
  isComplex?: boolean;
}

/**
 * Raw version info from Sefaria API
 */
export interface SefariaVersionApiResponse {
  versionTitle: string;
  shortVersionTitle?: string;
  language: string;
  isPrimary?: boolean;
  license?: string;
  status?: string;
  priority?: number;
}

/**
 * Raw link response from Sefaria API
 */
export interface SefariaLinkApiResponse {
  ref: string;
  anchorRef: string;
  category: string;
  collectiveTitle?: {
    en: string;
    he: string;
  };
  he: string;
  text?: string;
  sourceHasEn?: boolean;
}

// ============================================================
// Error Types
// ============================================================

/**
 * Custom error for rate limiting
 */
export class RateLimitError extends Error {
  constructor(message: string = 'API rate limited') {
    super(message);
    this.name = 'RateLimitError';
  }
}

/**
 * Custom error for API failures
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ============================================================
// Props Types for Components
// ============================================================

/**
 * Props for the main reader modal
 */
export interface SefariaReaderModalProps {
  /** Sefaria reference for the parsha, e.g., "Exodus.13.17-17.16" */
  parshaRef: string;
  /** English parsha title */
  parshaTitle: string;
  /** Hebrew parsha name */
  parshaHebrew: string;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Optional verse reference to scroll to and highlight on open */
  focusVerseRef?: string;
  /** Optional callback when user wants to navigate to a different parsha */
  onNavigateToParsha?: (
    parshaRef: string,
    parshaTitle: string,
    parshaHebrew: string,
    focusVerseRef: string
  ) => void;
}

/**
 * Props for individual verse display
 */
export interface VerseViewProps {
  /** The verse data */
  verse: SefariaVerse;
  /** Current layout mode */
  layout: ReaderLayout;
  /** Current font size */
  fontSize: ReaderFontSize;
  /** Whether this verse is highlighted */
  isHighlighted: boolean;
  /** Whether commentary is expanded for this verse */
  isCommentaryExpanded: boolean;
  /** Whether this verse is blinking (used when opening from favorites) */
  isBlinking?: boolean;
  /** Callback to toggle highlight */
  onHighlight: (ref: string) => void;
  /** Callback to toggle commentary expansion */
  onToggleCommentary: (ref: string) => void;
  /** User ID for favorites */
  userId?: string;
  /** Parsha context for favorites (e.g., "Parashat Beshalach") */
  parshaContext?: string;
  /** Full parsha reference for favorites (e.g., "Exodus.13.17-17.16") */
  parshaRef?: string;
  /** Hebrew parsha name for favorites (e.g., "בשלח") */
  parshaHebrew?: string;
}

/**
 * Props for commentary panel
 */
export interface CommentaryPanelProps {
  /** Verse reference */
  verseRef: string;
  /** Whether the panel is expanded */
  isExpanded: boolean;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * Props for reader toolbar
 */
export interface ReaderToolbarProps {
  /** Parsha title */
  title: string;
  /** Hebrew parsha name */
  hebrewTitle: string;
  /** Current parsha reference (e.g., "Exodus.13.17-17.16") */
  currentParshaRef: string;
  /** Current layout mode */
  layout: ReaderLayout;
  /** Current font size */
  fontSize: ReaderFontSize;
  /** Current verse position */
  currentVerse: number;
  /** Total verse count */
  totalVerses: number;
  /** Callback for layout change */
  onLayoutChange: (layout: ReaderLayout) => void;
  /** Callback for font size change */
  onFontSizeChange: (size: ReaderFontSize) => void;
  /** Callback for settings open */
  onOpenSettings: () => void;
  /** Callback for close */
  onClose: () => void;
  /** Callback to scroll to top */
  onScrollToTop: () => void;
  /** Callback to scroll to bottom */
  onScrollToBottom: () => void;
  /** Callback to jump to a verse within the current parsha */
  onJumpToVerse: (verseRef: string) => void;
  /** Callback to open a different parsha and focus on a verse */
  onOpenDifferentParsha: (
    parshaRef: string,
    parshaTitle: string,
    parshaHebrew: string,
    focusVerseRef: string
  ) => void;
}

/**
 * Props for settings panel
 */
export interface ReaderSettingsPanelProps {
  /** Whether the panel is visible */
  isOpen: boolean;
  /** Available translation versions */
  versions: SefariaVersion[];
  /** Currently selected version */
  selectedVersion: string;
  /** Number of highlighted verses */
  highlightedCount: number;
  /** Whether content is available offline */
  isAvailableOffline: boolean;
  /** Whether the user is currently offline */
  isOffline: boolean;
  /** Whether currently using cached data */
  isUsingCache: boolean;
  /** Callback for version change */
  onVersionChange: (version: string) => void;
  /** Callback to clear highlights */
  onClearHighlights: () => void;
  /** Callback to clear reading position */
  onClearPosition: () => void;
  /** Callback to close panel */
  onClose: () => void;
}
